import { Highlight } from '@curology/ui-components-web/react/Highlight';
import { Typography } from '@curology/ui-components-web/react/Typography';
import Image from 'next/image';
import React from 'react';

import { type Card } from '@/types/productSelectionAndCheckout/types';

import { IngredientsAccordion } from '../IngredientsAccordion';
import { ModalImage } from '../ModalImage';
import { ModalPricingVolumeAndProductOptionSelector } from '../ModalPricingVolumeAndProductOptionSelector';

export const BarrierBalmModalContent = ({ modalCard }: { modalCard: Card }) => {
  const { modal } = modalCard;

  return (
    <>
      <ModalImage modal={modal} />
      <Typography className="cur-mt-4" size="h2">
        {modal.title}
      </Typography>
      <ModalPricingVolumeAndProductOptionSelector modalCard={modalCard} />
      {modalCard.recommendationText && (
        <Highlight className="cur-mt-4" state="success">
          <div
            dangerouslySetInnerHTML={{
              __html: modalCard.recommendationText.modalCallout,
            }}
          ></div>
        </Highlight>
      )}
      <Typography className="cur-mt-4" size="h3">
        Why it&apos;s special
      </Typography>
      <Typography className="cur-mt-4">
        Skin in need of rescuing? Meet Barrier Balm—a versatile, non-greasy
        treatment that protects dry, sensitive skin from irritation, while
        promoting a healthy skin barrier.
      </Typography>
      <Typography className="cur-mt-4">
        Barrier Balm&apos;s silky, acne-friendly formula cocoons the skin with
        three key moisturizers (humectants, emollients, and occlusives) to
        promote a healthy skin barrier. These powerhouse ingredients work
        together to provide 360° skin-nourishing benefits to your face, hands,
        lips, elbows—anywhere that needs a little extra TLC.
      </Typography>
      <hr className="cur-bg-surface-disabled cur-border-0 cur-h-[1px] cur-mt-4" />
      <ul className="cur-flex cur-items-center cur-text-center cur-mt-4">
        <li className="cur-flex-1">
          <div className="cur-pr-2">
            <Image
              alt="Dermatologist badge"
              className="cur-h-6 cur-inline-block"
              height={24}
              src="https://images.ctfassets.net/8pzh4g8cs30t/35Tu0N4YJs6ACdp3K8oQo8/50fa0bc9b15e325647766218617dfa9c/barrier-balm-dermatologist-badge.png"
              width={24.5}
            />
            <Typography className="cur-mt-2" size="meta">
              Dermatologist Founded.
            </Typography>
            <Typography size="meta">Developed. Tested.</Typography>
          </div>
        </li>
        <li className="cur-flex-1">
          <div className="cur-pl-2">
            <Image
              alt="National Eczema Association badge"
              className="cur-h-6 cur-inline-block"
              height={24}
              src="https://images.ctfassets.net/8pzh4g8cs30t/6WWatKrzXfr02aLB7od8DM/d3dceb445074ae9480d5b73e91be0d00/barrier-balm-nea-badge.png"
              width={18}
            />
            <Typography className="cur-mt-2" size="meta">
              Accepted by the National
            </Typography>
            <Typography size="meta">Eczema Association</Typography>
          </div>
        </li>
      </ul>
      <hr className="cur-bg-surface-disabled cur-border-0 cur-h-[1px] cur-mt-4" />
      <Typography className="cur-mt-4" size="h3">
        How to use it
      </Typography>
      <Typography className="cur-mt-4">
        Apply generously morning, night, or throughout the day to your face, or
        particularly dry or sensitive areas of skin.
      </Typography>
      <Typography className="cur-mt-4">
        We recommend layering it on after either the moisturizer or the rich
        moisturizer to really lock in moisture. We also love to rub any excess
        into our hands and cuticles for some bonus protection (and relief)!
      </Typography>
      <Typography className="cur-mt-4" size="h3">
        Featured ingredients
      </Typography>
      <IngredientsAccordion
        body="Forms a barrier on the skin to minimize water loss"
        title="Dimethicone 13%"
      />
      <IngredientsAccordion
        body="Seals skin with a temporary water-protective barrier"
        title="Petrolatum 41.9%"
      />
      <IngredientsAccordion
        body="Softens skin and helps seal in moisture"
        title="Shea butter"
      />
      <IngredientsAccordion
        body="Attracts and retains moisture for visibly plumper skin"
        title="Hyaluronic acid"
      />
      <Typography className="cur-mt-4" size="h3">
        Full ingredients list
      </Typography>
      <Typography className="cur-mt-4">
        Active ingredients: Dimethicone 13%, Petrolatum 41.9%
      </Typography>
      <Typography className="cur-mt-4">
        Inactive ingredients: Caprylic/Capric Triglyceride, Butyrospermum Parkii
        (Shea Butter), Aluminum Starch Octenylsuccinate, Synthetic Wax,
        Polyhydroxystearic Acid, Sodium Hyaluronate, Water, Zinc Oxide,
        Tocopheryl Acetate, Triethoxycaprylylsilane
      </Typography>
    </>
  );
};
